import React, { useState, useEffect } from 'react';
import { sendApiRequest } from 'lib/api/public';
import { Box, Button } from '@mui/material';
import Loader from 'components/common/Loader';
import Modal from './Modal';

type Tag = {
  text: string;
  value: string;
  type: string;
};

const PlaceholderChooser = ({ onSelect }: { onSelect: (value: string) => void }) => {
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTagType, setSelectedTagType] = useState('');
  const [showTags, setShowTags] = useState(false);

  const getTagTypes = () => {
    setLoading(true);

    sendApiRequest({
      path: '/custom-fields/tags',
      method: 'GET',
    })
      .then(response => {
        const tagsByType = (response || {}) as Record<string, Tag[]>;

        let tags: Tag[] = [];

        for (const tagType of Object.keys(tagsByType)) {
          tags = [
            ...tags,
            ...tagsByType[tagType].map(tag => ({
              ...tag,
              type: tagType,
            })),
          ];
        }

        setSelectedTagType('Sender');
        setTags(tags);
      })
      .catch(() => {
        setTags([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getTagTypes();
  }, []);

  const renderTypes = () => {
    return (
      <Box
        sx={{
          width: '140px',
          alignSelf: 'flex-start',
          bgcolor: 'background.paper',
          padding: '2px 4px',
          borderRadius: '8px',
          position: 'sticky',
          top: '0',
          boxShadow: 1,
        }}
      >
        {['Sender', 'Candidate', 'Client', 'Contact', 'Job'].map(type => (
          <Box
            key={type}
            sx={{
              cursor: 'pointer',
              padding: '8px 12px',
              margin: '2px 0',
              borderRadius: '6px',
              ...(selectedTagType === type && {
                bgcolor: 'action.selected',
              }),
            }}
            onClick={() => setSelectedTagType(type)}
          >
            {type}
          </Box>
        ))}
      </Box>
    );
  };

  const renderTags = () => {
    const contentStyle = {
      flex: '1',
      bgcolor: 'background.paper',
      padding: '4px',
      borderRadius: '8px',
      boxShadow: 1,
      maxHeight: '500px',
      overflowY: 'auto',
    };

    return (
      <Box sx={contentStyle}>
        {tags
          .filter(tag => !selectedTagType || tag.type === selectedTagType)
          .map((tag, index) => (
            <Box
              key={index}
              sx={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderRadius: '6px',
                marginBottom: '2px',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
              onClick={() => {
                onSelect(tag.value);
                setShowTags(false);
              }}
            >
              {tag.text}
            </Box>
          ))}
      </Box>
    );
  };

  const renderPlaceholders = () => {
    if (!showTags) {
      return null;
    }

    return (
      <Modal
        open={showTags}
        onClose={() => setShowTags(false)}
        title="Add placeholder"
        renderContent={() => (
          <Box sx={{ display: 'flex', gap: 2 }}>
            {renderTypes()}
            {renderTags()}
          </Box>
        )}
      />
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      {renderPlaceholders()}

      <Button variant="contained" color="inherit" onClick={() => setShowTags(true)}>
        Add placeholder
      </Button>
    </>
  );
};

export default PlaceholderChooser;
